import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toReadableTotal, toReadableTimestamp } from '../utils/orderUtils';

import { Tooltip as ReactTooltip } from 'react-tooltip'
import 'react-tooltip/dist/react-tooltip.css'

import { fetchAuthSession } from 'aws-amplify/auth';
import config from '../config';
import useOrderActions from '../hooks/useOrderActions';
import usePdfManagement from '../hooks/usePdfManagement';
import usePrintManagement from '../hooks/usePrintManagement';
import useOrderEditing from '../hooks/useOrderEditing';
import useRefund from '../hooks/useRefund';

import OrderSummary from '../components/OrderDetails/OrderSummary';
import OrderCost from '../components/OrderDetails/OrderCost';
import RefundInfo from '../components/OrderDetails/RefundInfo';
import OrderShipping from '../components/OrderDetails/OrderShipping';
import OrderBilling from '../components/OrderDetails/OrderBilling';
import OrderDelivery from '../components/OrderDetails/OrderDelivery';
import OrderDiscount from '../components/OrderDetails/OrderDiscount';
import GiftDetails from '../components/OrderDetails/GiftDetails';

const OrderDetails = ({ testMode = false }) => {
  // const host = testMode ? config.testBackendUrl : config.backendUrl;
  const host = config.backendUrl;
  
  const [pdf, setPdf] = useState(null);
  const { orderId } = useParams();
  const [order, setOrder] = useState(null)
  const [luluIds, setLuluIds] = useState([])
  const [choiceLegend, setChoiceLegend] = useState({});
  const [loadingPdf, setLoadingPdf] = useState(false);

  const [tab, setTab] = useState('cost');
  const [expandedChoices, setExpandedChoices] = useState([]);
  const [authToken, setAuthToken] = useState('');

  const { terminateOrder, updateStatus, updateOrderNote } = useOrderActions(order, setOrder, authToken);
  const { refund } = useRefund({ order, authToken });
  const { generatePDF, clearPDF } = usePdfManagement({
    order,
    pdf,
    setPdf,
    authToken,
    setLoadingPdf,
  });

  const { sendToPrinter, cancelPrintJob } = usePrintManagement({
    order,
    luluIds,
    setLuluIds,
    authToken,
  });

  const {
    handleEditValue,
    handleEditShippingValue,
    updateChoices,
    updateNestedValue,
    handleEditQuantity
  } = useOrderEditing({ order, setOrder, orderId, authToken });

  useEffect(() => {
    console.log("Host URL:", host);
    const getAuthToken = async () => {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        setAuthToken(token);
      } catch (error) {
        console.error('Error getting auth token:', error);
      }
    };
    
    getAuthToken();
  }, []);

  useEffect(() => {
    if (!authToken) return; // Don't fetch if no token yet

    fetch(`${host}/api/admin/order/${orderId}`, {
      headers: {
        Authorization: `Bearer ${authToken}`
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    })
    .then(data => {
      console.log(data);
      setOrder(data);
      
      // Initialize PDF state with existing PDF URLs if available, otherwise null
      const initialPdfState = data.items.map(item => {
        if (item.pdf_url && item.pdf_url.expiry) {
          const now = new Date().getTime() / 1000;
          if (now < item.pdf_url.expiry) {
            return item.pdf_url;
          }
        }
        return null;
      });
      setPdf(initialPdfState);
      
      for (const item of data.items) {
        setLuluIds((prev) => [...prev, item.lulu_order_id]);
      }
    })
    .catch(error => {
      console.error('Error fetching order:', error);
    });
  }, [authToken, orderId]);

  // Fetch product legends for tooltip hints once order is loaded
  useEffect(() => {
    if (!order) return;

    const uniqueProductIds = Array.from(new Set(
      (order.items || []).map(item => item.readable_id).filter(Boolean)
    ));

    uniqueProductIds.forEach((productId) => {
      if (choiceLegend[productId]) return; // already loaded

      fetch(`${host}/api/products/${productId}`, {
        method: 'GET',
        headers: {
          'X-API-KEY': process.env.REACT_APP_GANDF_KEY,
          'Content-Type': 'application/json',
        },
      })
        .then((response) => response.json())
        .then((productData) => {
          setChoiceLegend((prev) => ({
            ...prev,
            [productId]: productData?.legend || {},
          }));
        })
        .catch((error) => {
          console.error('Error fetching product legend:', error);
        });
    });
  }, [order, host]);

  const checkPdfStatus = (orderId, item, attempts = 0) => {
    if (attempts >= 10) {
      console.log('Max attempts reached for PDF status check');
      return;
    }

    setTimeout(() => {
      fetch(`${host}/api/admin/order/${orderId}/pdf/${item.readable_id}`, {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      })
      .then(response => response.json())
      .then(data => {
        if (data.status === 'completed') {
          setPdf(prev => {
            const newPdf = [...prev];
            newPdf[order.items.indexOf(item)] = data;
            return newPdf;
          });
        } else if (data.status === 'failed') {
          console.log('PDF generation failed');
        } else {
          checkPdfStatus(orderId, item, attempts + 1);
        }
      })
      .catch(error => {
        console.error('Error checking PDF status:', error);
      });
    }, 2000);
  };

  const renderCell = (value, itemIndex, path = '') => {
    if (value === null || value === undefined) {
      return (
        <span
          style={{ cursor: 'pointer', color: 'var(--text-muted)', fontStyle: 'italic' }}
          onDoubleClick={() => handleEditValue(itemIndex, path, '')}
        >
          (empty)
        </span>
      );
    }

    if (Array.isArray(value)) {
      return (
        <ul style={{ margin: 0, paddingLeft: 'var(--spacing-lg)' }}>
          {value.map((item, idx) => (
            <li key={idx}>
              {renderCell(item, itemIndex, `${path}[${idx}]`)}
            </li>
          ))}
        </ul>
      );
    }

    if (typeof value === 'object') {
      return (
        <table className="table" style={{ margin: 0 }}>
          <tbody>
            {Object.keys(value).map((key, idx) => (
              <tr key={idx}>
                <td style={{ fontWeight: 'var(--font-weight-semibold)' }}>{key}</td>
                <td 
                  onDoubleClick={() => !value[key] && handleEditValue(itemIndex, path ? `${path}.${key}` : key, '')}
                  style={{ cursor: !value[key] ? 'pointer' : 'default' }}
                >
                  {renderCell(
                    value[key],
                    itemIndex,
                    path ? `${path}.${key}` : key
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    
    // For primitive values, make them editable
    return (
      <span
        style={{ cursor: 'pointer' }}
        onDoubleClick={() => handleEditValue(itemIndex, path, value)}
      >
        {value}
      </span>
    );
  };

  if (!order) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: 'var(--spacing-3xl)' }}>
          <div className="spinner" style={{ margin: '0 auto' }}></div>
          <p style={{ marginTop: 'var(--spacing-md)', color: 'var(--text-secondary)' }}>
            Loading order details...
          </p>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const statusMap = {
      'purchase_pending': { class: 'badge-warning', text: 'Pending' },
      'order_placed': { class: 'badge-success', text: 'Placed' },
      'sent_to_printer': { class: 'badge-info', text: 'Sent to Printer' },
      'printed': { class: 'badge-success', text: 'Printed' },
      'awaiting_delivery': { class: 'badge-warning', text: 'Awaiting Delivery' },
      'complete': { class: 'badge-success', text: 'Complete' },
      'cancelled': { class: 'badge-error', text: 'Cancelled' },
      'refunded': { class: 'badge-secondary', text: 'Refunded' }
    };
    
    const statusInfo = statusMap[status] || { class: 'badge-secondary', text: status };
    return <span className={`badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  return (
    <div className="main-content">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-header-title">
            Order Details
            {testMode && <span style={{color: 'var(--warning-color)'}}> (Test Mode)</span>}
          </h1>
          <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>
            Order ID: {order.id}
          </div>
        </div>
        <div className="page-header-actions">
          <Link to={testMode ? "/test/orders" : "/orders"} className="btn btn-secondary">
            ← Back to Orders
          </Link>
        </div>
      </div>

      {/* Order Summary Card */}
      <div className="card" style={{ marginBottom: 'var(--spacing-2xl)' }}>
        <div className="card-header">
          <h3 className="card-title">Order Summary</h3>
        </div>
        <div className="card-body">
          <OrderSummary order={order} />
        </div>
      </div>

      {/* Order Actions Card */}
      <div className="card" style={{ marginBottom: 'var(--spacing-2xl)' }}>
        <div className="card-header">
          <h3 className="card-title">Order Actions</h3>
        </div>
        <div className="card-body">
          <ReactTooltip id="status-legend" style={{ maxWidth: '300px' }} />
          <ReactTooltip id="send-all-to-printer" style={{ maxWidth: '300px' }} />
          <ReactTooltip id="send-to-printer" style={{ maxWidth: '300px' }} />
          <div style={{ display: 'flex', gap: 'var(--spacing-md)', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={updateStatus}>
              Update Status 
              <span 
                data-tooltip-id="status-legend" 
                data-tooltip-html="Status options:<br><ol><li>Order Placed: order_placed</li><li>Order Cancelled: cancelled</li><li>Sent to printer: sent_to_printer</li><li>Printed: printed</li><li>Awaiting delivery: awaiting_delivery</li><li>Delivered & Complete: complete</li></ol>" 
                className="info-icon" 
                style={{ marginLeft: 'var(--spacing-xs)' }}
              >
                ℹ️
              </span>
            </button>
            <button className="btn btn-secondary" onClick={() => sendToPrinter(-1)}>
              Send All to Printer
              <span 
                data-tooltip-id="send-all-to-printer"
                data-tooltip-html="Print job(s) not going through?<br><ol><li>Check Lulu in case the print job was created</li><li>Check cover and interior PDFs and regenerate if needed</li><li>Check shipping address (address line 1 must be less than 30 characters)</li></ol>"
                className="info-icon"
                style={{ marginLeft: 'var(--spacing-xs)' }}
              >
                ℹ️
              </span>
            </button>
            {luluIds.every(id => id === luluIds[0] && id !== null && id !== undefined) && (
              <button className="btn btn-secondary" onClick={() => cancelPrintJob(-1)}>
                Cancel Lulu Print Job
              </button>
            )}
            <button className="btn btn-danger" onClick={terminateOrder}>
              Cancel Order
            </button>
            <button className="btn btn-secondary" onClick={() => refund(order.cost.total)}>
              Full Refund
            </button>
            <button className="btn btn-secondary" onClick={() => refund()}>
              Partial Refund
            </button>
          </div>
        </div>
      </div>

      {/* Order Notes Card */}
      <div className="card" style={{ marginBottom: 'var(--spacing-2xl)' }}>
        <div className="card-header">
          <h3 className="card-title">Order Notes</h3>
        </div>
        <div className="card-body">
          <div className="form-group">
            <textarea 
              className="form-textarea"
              placeholder="Add a note to this order"
              onChange={(e) => setOrder({ ...order, notes: e.target.value })}
              value={order.notes || ''}
            />
            <button 
              className="btn btn-primary" 
              onClick={() => {
                const note = order.notes || '';
                if (note.length > 500) {
                  alert("Note is too long. Please keep it under 500 characters.");
                  return;
                }
                updateOrderNote(note);
              }}
            >
              Save Note
            </button>
          </div>
        </div>
      </div>

      {/* Order Details Tabs */}
      <div className="card" style={{ marginBottom: 'var(--spacing-2xl)' }}>
        <div className="card-header">
          <div style={{ display: 'flex', gap: 'var(--spacing-sm)', flexWrap: 'wrap' }}>
            <button 
              className={`btn ${tab === "cost" ? "btn-primary" : "btn-ghost"}`} 
              onClick={() => setTab("cost")}
            >
              Cost Breakdown
            </button>
            {(order.status === 'refunded' || order.status === 'partially_refunded' || order.status.includes("refunded") || order.refund) && (
              <button 
                className={`btn ${tab === "refund" ? "btn-primary" : "btn-ghost"}`} 
                onClick={() => setTab("refund")}
              >
                Refund Details
              </button>
            )}
            {order.discount && (
              <button 
                className={`btn ${tab === "discount" ? "btn-primary" : "btn-ghost"}`} 
                onClick={() => setTab("discount")}
              >
                Discount Details
              </button>
            )}
            <button 
              className={`btn ${tab === "billing" ? "btn-primary" : "btn-ghost"}`} 
              onClick={() => setTab("billing")}
            >
              Billing Details
            </button>
            <button 
              className={`btn ${tab === "shipping" ? "btn-primary" : "btn-ghost"}`} 
              onClick={() => setTab("shipping")}
            >
              Shipping Details
            </button>
            <button 
              className={`btn ${tab === "delivery" ? "btn-primary" : "btn-ghost"}`} 
              onClick={() => setTab("delivery")}
            >
              Estimated Delivery
            </button>
            {order.gift_purchase && (
              <button
                className={`btn ${tab === "gift" ? "btn-primary" : "btn-ghost"}`}
                onClick={() => setTab("gift")}
              >
                Gift Details
              </button>
            )}
          </div>
        </div>
        <div className="card-body">
          {tab === "cost" && <OrderCost order={order} />}
          {tab === "discount" && order.discount && <OrderDiscount order={order} />}
          {tab === "refund" && (order.status === 'refunded' || order.status === 'partially_refunded' || order.status.includes("refunded") || order.refund) && (
            <RefundInfo order={order} handleEditShippingValue={handleEditShippingValue} />
          )}
          {tab === "billing" && <OrderBilling order={order} handleEditShippingValue={handleEditShippingValue} />}
          {tab === "shipping" && order.shipping && order.shipping.name && order.shipping.address && (
            <OrderShipping order={order} handleEditShippingValue={handleEditShippingValue} />
          )}
          {order.shipping.estimated_delivery && tab === "delivery" && <OrderDelivery order={order} />}
          {order.gift_purchase && tab === "gift" && <GiftDetails order={order} testMode={testMode} />}
        </div>
      </div>

      {/* Items Ordered */}
      <div className="card" style={{ marginBottom: 'var(--spacing-2xl)' }}>
        <div className="card-header">
          <h3 className="card-title">Items Ordered</h3>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <div className="table-container" style={{ margin: 0, borderRadius: 0 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Product ID</th>
                  <th>Product Name</th>
                  <th>Product Type</th>
                  <th>
                    Quantity
                    <>
                      <ReactTooltip id={`quantity-tooltip`} />
                      <span
                        data-tooltip-id={`quantity-tooltip`}
                        data-tooltip-html="Double click on the quantity to edit it. This will NOT update the total cost of the order. User must be billed manually.<br><br>Quantity will only reflect in Lulu if the order is sent to printer after the change."
                        className="info-icon"
                        style={{ marginLeft: 'var(--spacing-xs)' }}
                      >
                        ℹ️
                      </span>
                    </>
                  </th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((product, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: 'var(--font-family-mono)', fontWeight: 'var(--font-weight-medium)' }}>
                      {product.readable_id}
                    </td>
                    <td style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)' }}>
                      {product.name}
                    </td>
                    <td>{product.variation.bookType}</td>
                    <td 
                      onDoubleClick={() => handleEditQuantity(i, product.quantity)}>
                      {product.quantity}
                    </td>
                    <td style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)' }}>
                      ${toReadableTotal(product.price * product.quantity)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Individual Items */}
      {order.items.map((item, i) => (
        <div key={i} className="card" style={{ marginBottom: 'var(--spacing-2xl)' }}>
          <div className="card-header">
            <h3 className="card-title">Item {i + 1}: {item.name}</h3>
          </div>
          <div className="card-body">
            {/* Item Status and Actions */}
            <div className="table-container" style={{ marginBottom: 'var(--spacing-lg)' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Order Actions</th>
                    <th>PDF</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ textAlign: "center" }}>
                      {getStatusBadge(item.status || 'none')}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <div style={{ display: 'flex', gap: 'var(--spacing-sm)', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button 
                          className="btn btn-sm btn-primary" 
                          onClick={() => generatePDF(i)}
                          disabled={loadingPdf}
                        >
                          {!loadingPdf ? 'Generate PDF' : 'Loading...'}
                        </button>
                        <button 
                          className="btn btn-sm btn-secondary" 
                          onClick={() => sendToPrinter(i)}
                        >
                          Send to Printer
                          <span 
                            data-tooltip-id={`send-to-printer`}
                            data-tooltip-html="Print job(s) not going through?<br><ol><li>Check Lulu in case the print job was created</li><li>Check cover and interior PDFs and regenerate if needed</li><li>Check shipping address (address line 1 must be less than 30 characters)</li></ol>"
                            className="info-icon"
                            style={{ marginLeft: 'var(--spacing-xs)' }}
                          >
                            ℹ️
                          </span>
                        </button>
                        {luluIds[i] !== null && luluIds[i] !== undefined && (
                          <button 
                            className="btn btn-sm btn-danger" 
                            onClick={() => cancelPrintJob(i)}
                          >
                            Cancel Print Job
                          </button>
                        )}
                      </div>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {pdf[i] && (
                        <div style={{ display: 'flex', gap: 'var(--spacing-sm)', justifyContent: 'center', flexWrap: 'wrap' }}>
                          {pdf[i]['url']['cover'] && pdf[i]['url']['interior'] && (
                            <>
                              <a 
                                href={pdf[i]['url']['cover']} 
                                target="_blank" 
                                rel="noreferrer"
                                className="btn btn-sm btn-secondary"
                              >
                                Download Cover
                              </a>
                              <a 
                                href={pdf[i]['url']['interior']} 
                                target="_blank" 
                                rel="noreferrer"
                                className="btn btn-sm btn-secondary"
                              >
                                Download Interior
                              </a>
                            </>
                          )}
                          {!pdf[i]['url']['cover'] && !pdf[i]['url']['interior'] && (
                            <a 
                              href={pdf[i]['url']} 
                              target="_blank" 
                              rel="noreferrer"
                              className="btn btn-sm btn-secondary"
                            >
                              Download PDF
                            </a>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* User Choices */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-md)' }}>
                <h4 style={{ margin: 0 }}>User Choices</h4>
                <button 
                  className="btn btn-sm btn-ghost" 
                  onClick={() => setExpandedChoices((prev) => {
                    return prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i];
                  })}
                >
                  {expandedChoices.includes(i) ? 'Hide Choices' : 'Show Choices'}
                </button>
              </div>

              {expandedChoices.includes(i) && (
                <div className="table-container">
                  <table className="table">
                    <tbody>
                      {Object.keys(item.choices).sort().map((key, index) => (
                        <tr key={index}>
                          <td style={{ fontWeight: 'var(--font-weight-semibold)' }}>
                            {key}
                            {choiceLegend[item.readable_id] && choiceLegend[item.readable_id][key] && (
                              <>
                                <ReactTooltip id={`${item.readable_id}-${key}`} style={{ maxWidth: '300px' }} />
                                <span
                                  data-tooltip-id={`${item.readable_id}-${key}`}
                                  data-tooltip-html={choiceLegend[item.readable_id][key]}
                                  className="info-icon"
                                  style={{ marginLeft: 'var(--spacing-xs)' }}
                                >
                                  ℹ️
                                </span>
                              </>
                            )}
                          </td>
                          <td
                            onDoubleClick={() => !item.choices[key] && handleEditValue(i, key, '')}
                            style={{ cursor: !item.choices[key] ? 'pointer' : 'default' }}
                          >
                            {renderCell(item.choices[key], i, key)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* Order Logs */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Order Logs</h3>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {order.logs && order.logs.length > 0 ? (
            <div className="table-container" style={{ margin: 0, borderRadius: 0 }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Action</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {order.logs.map((log, index) => (
                    <tr key={index}>
                      <td style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-sm)' }}>
                        {toReadableTimestamp(log.timestamp)}
                      </td>
                      <td style={{ fontWeight: 'var(--font-weight-medium)' }}>
                        {log.action}
                      </td>
                      <td>{log.details}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 'var(--spacing-3xl)' }}>
              <div style={{ fontSize: '3rem', marginBottom: 'var(--spacing-lg)' }}>📋</div>
              <h3 style={{ marginBottom: 'var(--spacing-md)' }}>No Logs Available</h3>
              <p style={{ color: 'var(--text-secondary)' }}>
                No activity logs are available for this order.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;